// lib
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import useNonInitialEffect from '@aytm/app/stats/hooks/useNonInitialEffect'

import { toggleCheckbox } from '@aytm/app/stats/page/sidebar/components/lib/utils'

// components
import { Checkbox } from 'primereact/checkbox'

// styles
import style from '@aytm/app/stats/page/sidebar/view-options/styles/ViewOptionsPanel.module.scss'

const Params = ({ model, features, onParamsChange, onToggleQuestions, onToggleAllCjLite }) => {
  const { chartMode, params } = model

  const [chartLegendMode, setChartLegendMode] = useState('long' == params.chartLegendMode || false)
  const [rType, setRType] = useState('responses' == params.rType || false)
  const [qLogicVisibility, setQLogicVisibility] = useState('hide' == params.qLogicVisibility || false)
  const [qAliasesVisibility, setQAliasesVisibility] = useState('hide' == params.qAliasesVisibility || false)
  const [zoomLevel, setZoomLevel] = useState('max' == params.zoomLevel || false)

  useEffect(() => {
    setChartLegendMode('long' == params.chartLegendMode || false)
  }, [params.chartLegendMode])
  useEffect(() => {
    setRType('responses' == params.rType || false)
  }, [params.rType])
  useEffect(() => {
    setQLogicVisibility('hide' == params.qLogicVisibility || false)
  }, [params.qLogicVisibility])
  useEffect(() => {
    setQAliasesVisibility('hide' == params.qAliasesVisibility || false)
  }, [params.qAliasesVisibility])
  useEffect(() => {
    setZoomLevel('max' == params.zoomLevel || false)
  }, [params.zoomLevel])

  useNonInitialEffect(() => !qLogicVisibility && setQAliasesVisibility(qLogicVisibility), [qLogicVisibility])

  useNonInitialEffect(
    () =>
      onParamsChange({
        chartLegendMode: (chartLegendMode && 'long') || 'short',
        rType: (rType && 'responses') || 'cases',
        qLogicVisibility: (qLogicVisibility && 'hide') || 'show',
        qAliasesVisibility: (qAliasesVisibility && 'hide') || 'show',
        zoomLevel: (zoomLevel && 'max') || 'min'
      }),
    [chartLegendMode, rType, qLogicVisibility, qAliasesVisibility, zoomLevel, onParamsChange]
  )

  return (
    <>
      <div className={`${style.row} ${style['section-title']}`}>Other view options</div>
      <div
        className={classNames(`${style.row} ${style.param}`, {
          [style['param-disabled']]: features.view_options.disabled
        })}
      >
        <Checkbox
          value="legend-mode"
          checked={chartLegendMode}
          disabled={features.view_options.disabled}
          onChange={features.view_options.disabled ? null : () => setChartLegendMode(!chartLegendMode)}
          onKeyDown={
            features.view_options.disabled
              ? null
              : event => toggleCheckbox(event, () => setChartLegendMode(!chartLegendMode))
          }
        />
        <span
          className={classNames(style['param-label'], { 'p-disabled': features.view_options.disabled })}
          onClick={features.view_options.disabled ? null : () => setChartLegendMode(!chartLegendMode)}
        >
          Use Answers as labels instead of {'pie' == chartMode ? '%' : '"A#"'}
        </span>
      </div>
      <div
        className={classNames(`${style.row} ${style.param}`, {
          [style['param-disabled']]: features.view_options.disabled
        })}
      >
        <Checkbox
          value="rtype"
          checked={rType}
          disabled={features.view_options.disabled}
          onChange={features.view_options.disabled ? null : () => setRType(!rType)}
          onKeyDown={features.view_options.disabled ? null : event => toggleCheckbox(event, () => setRType(!rType))}
        />
        <span
          className={classNames(style['param-label'], { 'p-disabled': features.view_options.disabled })}
          onClick={features.view_options.disabled ? null : () => setRType(!rType)}
        >
          Count responses in multiple choice Qs
        </span>
      </div>
      {features.qlogic_visibility.hidden ? null : (
        <div
          className={classNames(`${style.row} ${style.param}`, {
            [style['param-disabled']]: features.view_options.disabled
          })}
        >
          <Checkbox
            value="qlogic-visibility"
            checked={qLogicVisibility}
            disabled={features.view_options.disabled}
            onChange={features.view_options.disabled ? null : () => setQLogicVisibility(!qLogicVisibility)}
            onKeyDown={
              features.view_options.disabled
                ? null
                : event => toggleCheckbox(event, () => setQLogicVisibility(!qLogicVisibility))
            }
            data-qlogic-visibility
          />
          <span
            className={classNames(style['param-label'], { 'p-disabled': features.view_options.disabled })}
            onClick={features.view_options.disabled ? null : () => setQLogicVisibility(!qLogicVisibility)}
          >
            Hide aytm Logic
          </span>
        </div>
      )}
      {features.qaliases_visibility.hidden || !qLogicVisibility ? null : (
        <div
          className={classNames(`${style.row} ${style.param}`, {
            [style['param-disabled']]: features.view_options.disabled
          })}
        >
          <Checkbox
            value="qaliases-visibility"
            checked={qAliasesVisibility}
            disabled={features.view_options.disabled}
            onChange={features.view_options.disabled ? null : () => setQAliasesVisibility(!qAliasesVisibility)}
            onKeyDown={
              features.view_options.disabled
                ? null
                : event => toggleCheckbox(event, () => setQAliasesVisibility(!qAliasesVisibility))
            }
          />
          <span
            className={classNames(style['param-label'], { 'p-disabled': features.view_options.disabled })}
            onClick={features.view_options.disabled ? null : () => setQAliasesVisibility(!qAliasesVisibility)}
          >
            Hide @aliases
          </span>
        </div>
      )}
      <div
        className={classNames(`${style.row} ${style.param}`, {
          [style['param-disabled']]: features.zoom_level.disabled
        })}
      >
        <Checkbox
          value="zoom-charts"
          checked={zoomLevel}
          disabled={features.zoom_level.disabled}
          onChange={features.zoom_level.disabled ? null : () => setZoomLevel(!zoomLevel)}
          onKeyDown={
            features.zoom_level.disabled ? null : event => toggleCheckbox(event, () => setZoomLevel(!zoomLevel))
          }
        />
        <span
          className={classNames(style['param-label'], { 'p-disabled': features.zoom_level.disabled })}
          onClick={features.zoom_level.disabled ? null : () => setZoomLevel(!zoomLevel)}
        >
          Zoom out all charts to full scale
        </span>
      </div>
      <div
        className={classNames(`${style.row} ${style.param}`, {
          [style['param-disabled']]: features.toggling.disabled
        })}
      >
        <Checkbox
          data-view-toggle-collapse-all
          value="qcollapse"
          checked={params.qCollapsed}
          disabled={features.toggling.disabled}
          onChange={features.toggling.disabled ? null : () => onToggleQuestions(!params.qCollapsed)}
          onKeyDown={
            features.toggling.disabled
              ? null
              : event => toggleCheckbox(event, () => onToggleQuestions(!params.qCollapsed))
          }
        />
        <span
          className={classNames(style['param-label'], { 'p-disabled': features.toggling.disabled })}
          onClick={features.toggling.disabled ? null : () => onToggleQuestions(!params.qCollapsed)}
        >
          Collapse all questions
        </span>
      </div>
      {features.cj_global_lite?.hidden ? null : (
        <div
          className={classNames(`${style.row} ${style.param}`, {
            [style['param-disabled']]: features.cj_global_lite?.disabled
          })}
        >
          <Checkbox
            data-view-toggle-cj-lite
            value="cj-global-lite"
            checked={Boolean(params.allCjLite)}
            disabled={features.cj_global_lite?.disabled}
            onChange={features.cj_global_lite?.disabled ? null : () => onToggleAllCjLite(!params.allCjLite)}
            onKeyDown={
              features.cj_global_lite?.disabled
                ? null
                : event => toggleCheckbox(event, () => onToggleAllCjLite(!params.allCjLite))
            }
          />
          <span
            className={classNames(style['param-label'], { 'p-disabled': features.cj_global_lite?.disabled })}
            onClick={features.cj_global_lite?.disabled ? null : () => onToggleAllCjLite(!params.allCjLite)}
          >
            Show all Conjoints in Attribute view
          </span>
        </div>
      )}
    </>
  )
}

Params.propTypes = {
  model: PropTypes.object,
  features: PropTypes.object,
  disabled: PropTypes.bool,
  onParamsChange: PropTypes.func,
  onToggleQuestions: PropTypes.func,
  onToggleAllCjLite: PropTypes.func
}

Params.displayName = 'Params'

export default Params
